<style scoped>
.progress-bar {
  width: 35%;
  min-width: 100px;
}
</style>

<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.agencyCode"
                    :label="$t('postalCodes.agency')"
                    v-if="!$userIsAdmin()"
                    disabled
                  ></v-text-field>
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    v-if="$userIsAdmin()"
                    :items="agenciesFilterItems"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('postalCodes.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="4" lg="4" cols="12">
                  <v-dialog
                    v-model="modalFilterDateFrom"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterDateFrom = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="dateFromFormatted"
                        :label="$t('statistics.dateFrom')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.dateFrom = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.dateFrom"
                      scrollable
                      @input="modalFilterDateFrom = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterDateFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="4" lg="4" cols="12">
                  <v-dialog
                    v-model="modalFilterDateTo"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterDateTo = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="dateToFormatted"
                        :label="$t('statistics.dateTo')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.dateTo = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.dateTo"
                      scrollable
                      @input="modalFilterDateTo = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterDateTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="4" lg="4" cols="12">
                  <v-select
                    v-model="filter.groupBy"
                    :items="groupByItemsValues"
                    :label="$t('statistics.groupBy')"
                  ></v-select>
                </v-col>
                <v-col sm="4" lg="4" cols="12">
                  <v-select
                    v-model="orderBy"
                    :items="orderByItemsValues"
                    :label="$t('statistics.orderBy')"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="search()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/timeSlots/statistics-grouped"
              :filter="this.filter"
              :filterExport="filterExport"
              :processFilter="processFilter"
              itemsPerPage="10000"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr
                v-for="item in items"
                :key="item.agencyCode + item.zoneCode + item.date + item.name"
              >
                <td>
                  <ExportExcel
                    url="/timeSlots/statistics-single"
                    :filter="rowExportFilter"
                    :processFilter="processRowExportFilter"
                    :filterExport="filterExport"
                    :item="item"
                  />
                </td>
                <td>{{ item.agencyCode }}</td>
                <td>{{ transformDate(item.date, $t("global.dateFormat")) }}</td>
                <td v-if="resultLevel != 'summary'">{{ item.zoneCode }}</td>
                <td v-if="resultLevel == 'detail'">{{ item.name }}</td>
                <td v-if="resultLevel == 'detail'">
                  {{ minutesToHour(item.hourFrom) }} -
                  {{ minutesToHour(item.hourTo) }}
                </td>
                <td>{{ minutesFormat(item.plannedDeliveryTime) }}</td>
                <td>{{ minutesFormat(item.availableDeliveryTime) }}</td>
                <td class="progress-bar">
                  <v-progress-linear
                    :value="item.occupation"
                    :color="
                      item.occupation >= 90
                        ? 'pink'
                        : item.occupation >= 70
                        ? 'amber'
                        : 'green'
                    "
                    height="25"
                    ><strong
                      >{{ Math.ceil(item.occupation) }}%</strong
                    ></v-progress-linear
                  >
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import { transformDate, minutesToHour, minutesFormat } from "@/js/utils.js";

const CONTEXT_AGENCIES = "/agencies";
const CONTEXT = "/timeSlots";

export default {
  components: {
    CrudButton,
    ExportExcel
  },
  name: "Statistics",
  data: () => ({
    loading: false,
    searched: false,
    resultLevel: null,
    items: [],
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    modalFilterDateFrom: false,
    modalFilterDateTo: false,
    dateFromFormatted: "",
    dateToFormatted: "",
    orderBy: "day",
    filter: {
      dateFrom: null,
      dateTo: null,
      agencyCode: "",
      groupBy: "detail"
    },
    rowExportFilter: {
      timeSlotCode: null,
      agencyCode: null,
      zoneCode: null,
      date: null
    },
    agencies: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    itemData: {
      days: [],
      daysFormat: [],
      expeditions: [],
      schedules: []
    }
  }),
  computed: {
    groupByItemsValues: function() {
      return [
        { text: this.$t("statistics.groupBy.detail"), value: "detail" },
        { text: this.$t("statistics.groupBy.day"), value: "day" },
        { text: this.$t("statistics.groupBy.summary"), value: "summary" }
      ];
    },
    orderByItemsValues: function() {
      return [
        { text: this.$t("statistics.orderBy.day"), value: "day" },
        { text: this.$t("statistics.orderBy.zone"), value: "zone" }
      ];
    },
    agenciesFilterItems() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    headers() {
      let headsResult = [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("timeSlots.datatable.agency"),
          value: "agencyCode",
          sortable: false
        },
        {
          text: this.$t("statistics.orderBy.day"),
          value: "date",
          sortable: false
        }
      ];

      if (this.resultLevel != "summary") {
        headsResult.push({
          text: this.$t("timeSlots.datatable.zone"),
          value: "zoneCode",
          sortable: false
        });

        if (this.resultLevel != "day") {
          headsResult.push(
            {
              text: this.$t("timeSlots.datatable.name"),
              value: "name",
              sortable: false
            },
            {
              text: this.$t("timeSlots.datatable.timeSlot"),
              value: "hourFrom",
              sortable: false
            }
          );
        }
      }

      headsResult.push(
        {
          text: this.$t("statistics.datatable.plannedDeliveryTime"),
          value: "plannedDeliveryTime",
          sortable: false
        },
        {
          text: this.$t("statistics.datatable.availableDeliveryTime"),
          value: "availableDeliveryTime",
          sortable: false
        },
        {
          text: this.$t("statistics.datatable.occupation"),
          value: "occupation",
          sortable: false
        }
      );

      return headsResult;
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.dateFrom": function() {
      this.dateFromFormatted = this.transformDate(
        this.filter.dateFrom,
        this.$t("global.dateFormat")
      );
    },
    "filter.dateTo": function() {
      this.dateToFormatted = this.transformDate(
        this.filter.dateTo,
        this.$t("global.dateFormat")
      );
    },
    "agencies.search": function(value) {
      console.debug("agencies.search");
      let aditionalFilter = {
        countryCode: this.item.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.agencies,
        CONTEXT_AGENCIES,
        aditionalFilter
      );
    },
    "agenciesFilter.search": function(value) {
      console.debug("agenciesFilter.search");
      let aditionalFilter = {
        countryCode: this.filter.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES,
        aditionalFilter
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      this.searched = false;

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      this.processFilter();

      localhttp
        .post(CONTEXT + "/statistics-grouped", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
          this.searched = true;
          this.resultLevel = this.filter.groupBy;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    processFilter() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      //custom sortBy
      if (this.orderBy != null) {
        this.filter.sortBy = [];
        this.filter.sortBy.push(this.orderBy);
      }
      if (this.filter.sortDesc.length == 0) {
        this.filter.sortDesc.push("ASC");
      }
    },
    processRowExportFilter(item) {
      this.rowExportFilter.date = item.date;
      this.rowExportFilter.zoneCode = item.zoneCode;
      this.rowExportFilter.timeSlotCode = item.code;
      this.rowExportFilter.agencyCode = item.agencyCode;
    },
    filterExport(jsonData) {
      var vm = this;
      jsonData.forEach(function(v) {
        if (v.hourFrom) {
          v.hourFrom = minutesToHour(v.hourFrom);
        }
        if (v.hourTo) {
          v.hourTo = minutesToHour(v.hourTo);
        }
        if (v.deliveryTime) {
          v.deliveryTime = minutesToHour(v.deliveryTime);
        }
        v.date = transformDate(v.date, vm.$t("global.dateFormat"));
      });
    },
    transformDate,
    minutesToHour,
    minutesFormat
  },
  mounted() {
    this.filter.dateFrom = new Date().toISOString().slice(0, 10);
    this.filter.dateTo = this.filter.dateFrom;
  },
  beforeMount() {
    this.filter.agencyCode = this.$userIsAdmin()
      ? ""
      : this.$keycloak.tokenParsed.agency;
  }
};
</script>
